import React from "react";
import { CCol, CFormGroup, CInput, CLabel, CRow, CSelect } from "@coreui/react";
import { useCreditNoteFormContext } from "../context/CreditNoteFormContext";

import { INVOICE_STATUS_APPROVED_BY_SET } from "../../../models/invoice";
import {
  ASSOCIATED_DOCUMENT_TYPE_CHOICES,
  ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE,
  ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE,
} from "../../../models/associated-document";
import { getFieldErrors } from "../../../models/errors";
import { FieldErrors } from "../../form/FieldErrors";
import InvoiceSelect from "../../invoice/InvoiceSelect";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "16px", marginTop: "16px" }}>
    <div style={{ width: "4px", height: "18px", background: "#f9b115", borderRadius: "2px", marginRight: "10px", flexShrink: 0 }} />
    <i className={`fa ${icon} mr-2`} style={{ fontSize: "0.85rem", color: "#f9b115" }}></i>
    <h6 style={{ margin: 0, fontWeight: 600, color: "var(--theme-muted)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.8px" }}>
      {label}
    </h6>
    <div style={{ flex: 1, height: "1px", background: "var(--theme-divider)", marginLeft: "10px" }} />
  </div>
);

const FormLabel: React.FC<{ required?: boolean; children: React.ReactNode }> = ({ required, children }) => (
  <CLabel className="mb-0" style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}>
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

export const CreditNoteFormAssociatedDocumentSection: React.FC = () => {
  const {
    creditNoteRef,
    associatedDocumentType,
    electronicInvoice,
    client,
    errors,
    readonly,
    onAssociatedDocumentTypeChange,
    onElectronicInvoiceChange,
    onAssociatedDocumentStampIdentifierChange,
    onAssociatedDocumentEstablishmentCodeChange,
    onAssociatedDocumentDispatchPointCodeChange,
    onAssociatedDocumentNumberChange,
    onAssociatedDocumentDateChange,
  } = useCreditNoteFormContext();

  const showElectronicInvoiceSelect =
    associatedDocumentType === ASSOCIATED_DOCUMENT_TYPE_ELECTRONIC_INVOICE;
  const showPrintedInvoiceFields =
    associatedDocumentType === ASSOCIATED_DOCUMENT_TYPE_PRINTED_INVOICE;

  return (
    <>
      <SectionDivider icon="fa-link" label="Documento Asociado" />
      <div
        style={{
          background: "var(--theme-warning-surface)",
          border: "1px solid var(--theme-warning-border)",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "16px",
        }}
      >
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <FormLabel required>Tipo Documento Asociado:</FormLabel>
            </CCol>
            <CCol md={4}>
              <CSelect
                type="text"
                defaultValue={
                  creditNoteRef.current
                    ? creditNoteRef.current.associatedDocumentType
                    : undefined
                }
                onChange={onAssociatedDocumentTypeChange}
                disabled={readonly}
              >
                <option value={""}>-----</option>
                {Array.from(ASSOCIATED_DOCUMENT_TYPE_CHOICES.entries()).map(
                  (entry) => {
                    return (
                      <option key={entry[0]} value={entry[0]}>
                        {entry[1]}
                      </option>
                    );
                  }
                )}
              </CSelect>
              <FieldErrors
                errors={
                  getFieldErrors("associatedDocumentType", errors) as string[]
                }
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {showElectronicInvoiceSelect ? (
          <CFormGroup className="mb-0">
            <CRow className="align-items-center">
              <CCol md={2}>
                <FormLabel required>Factura Electrónica:</FormLabel>
              </CCol>
              <CCol>
                <fieldset disabled={readonly}>
                  <InvoiceSelect
                    onChange={onElectronicInvoiceChange}
                    value={electronicInvoice}
                    status={INVOICE_STATUS_APPROVED_BY_SET}
                    clientId={client?.id}
                  />
                </fieldset>
                <FieldErrors
                  errors={getFieldErrors("invoiceId", errors) as string[]}
                ></FieldErrors>
                <FieldErrors
                  errors={
                    getFieldErrors("associatedDocumentCdc", errors) as string[]
                  }
                ></FieldErrors>
              </CCol>
            </CRow>
          </CFormGroup>
        ) : null}
        {showPrintedInvoiceFields ? (
          <>
            <CFormGroup>
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Timbrado:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    defaultValue={
                      creditNoteRef.current
                        ? creditNoteRef.current.associatedDocumentStampIdentifier
                        : undefined
                    }
                    onChange={onAssociatedDocumentStampIdentifierChange}
                    placeholder="Timbrado"
                    disabled={readonly}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "associatedDocumentStampIdentifier",
                        errors
                      ) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <FormLabel required>Establecimiento:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    defaultValue={
                      creditNoteRef.current
                        ? creditNoteRef.current.associatedDocumentEstablishmentCode
                        : undefined
                    }
                    onChange={onAssociatedDocumentEstablishmentCodeChange}
                    placeholder="Cod"
                    disabled={readonly}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "associatedDocumentEstablishmentCode",
                        errors
                      ) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <FormLabel required>Punto Expedición:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    defaultValue={
                      creditNoteRef.current
                        ? creditNoteRef.current.associatedDocumentDispatchPointCode
                        : undefined
                    }
                    onChange={onAssociatedDocumentDispatchPointCodeChange}
                    placeholder="Cod"
                    disabled={readonly}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "associatedDocumentDispatchPointCode",
                        errors
                      ) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
            <CFormGroup className="mb-0">
              <CRow className="align-items-center">
                <CCol md={2}>
                  <FormLabel required>Nro. Factura:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="text"
                    defaultValue={
                      creditNoteRef.current
                        ? creditNoteRef.current.associatedDocumentNumber
                        : undefined
                    }
                    onChange={onAssociatedDocumentNumberChange}
                    placeholder="Nro"
                    disabled={readonly}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors(
                        "associatedDocumentNumber",
                        errors
                      ) as string[]
                    }
                  ></FieldErrors>
                </CCol>
                <CCol md={2}>
                  <FormLabel required>Fecha Factura:</FormLabel>
                </CCol>
                <CCol md={2}>
                  <CInput
                    type="date"
                    defaultValue={
                      creditNoteRef.current
                        ? creditNoteRef.current.associatedDocumentDate
                        : undefined
                    }
                    onChange={onAssociatedDocumentDateChange}
                    placeholder="Fecha"
                    disabled={readonly}
                  ></CInput>
                  <FieldErrors
                    errors={
                      getFieldErrors("associatedDocumentDate", errors) as string[]
                    }
                  ></FieldErrors>
                </CCol>
              </CRow>
            </CFormGroup>
          </>
        ) : null}
      </div>
    </>
  );
};
